import Phaser from 'phaser';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { Button } from './Button.js';

export interface StepperOptions {
  readonly x: number;
  readonly y: number;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly label?: string;
  readonly onChange: (value: number) => void;
}

const STEP_BTN_SIZE = 56;
const VALUE_TEXT_WIDTH = 60;
const LABEL_GAP = 14;

/**
 * Compact horizontal stepper:  Label  [−]  N  [+]
 * Used for the attack dice picker and the post-conquest advance count.
 * Range is mutable; setValue clamps into the current range.
 */
export class Stepper {
  private readonly labelText: Phaser.GameObjects.Text;
  private readonly valueText: Phaser.GameObjects.Text;
  private readonly minusButton: Button;
  private readonly plusButton: Button;
  private value: number;
  private min: number;
  private max: number;
  private onChange: (value: number) => void;

  constructor(scene: Phaser.Scene, opts: StepperOptions) {
    this.value = opts.value;
    this.min = opts.min;
    this.max = opts.max;
    this.onChange = opts.onChange;

    this.labelText = scene.add
      .text(opts.x, opts.y, (opts.label ?? '').toUpperCase(), {
        fontFamily: FONT_HEADER,
        fontSize: '15px',
        color: COLORS.textSecondary,
        fontStyle: '600',
      })
      .setLetterSpacing(2)
      .setOrigin(1, 0.5); // right-align so it sits flush left of the minus button

    this.minusButton = new Button(scene, {
      x: opts.x + LABEL_GAP + STEP_BTN_SIZE / 2,
      y: opts.y,
      width: STEP_BTN_SIZE,
      height: STEP_BTN_SIZE,
      text: '−',
      onTap: () => this.step(-1),
    });

    this.valueText = scene.add
      .text(
        opts.x + LABEL_GAP + STEP_BTN_SIZE + VALUE_TEXT_WIDTH / 2,
        opts.y,
        String(opts.value),
        {
          fontFamily: FONT_MONO,
          fontSize: '30px',
          color: COLORS.textPrimary,
          fontStyle: '700',
        }
      )
      .setOrigin(0.5);

    this.plusButton = new Button(scene, {
      x: opts.x + LABEL_GAP + STEP_BTN_SIZE + VALUE_TEXT_WIDTH + STEP_BTN_SIZE / 2,
      y: opts.y,
      width: STEP_BTN_SIZE,
      height: STEP_BTN_SIZE,
      text: '+',
      onTap: () => this.step(+1),
    });

    this.refreshButtons();
  }

  setRange(min: number, max: number): void {
    this.min = min;
    this.max = max;
    if (this.value < min) this.value = min;
    if (this.value > max) this.value = max;
    this.valueText.setText(String(this.value));
    this.refreshButtons();
  }

  setValue(value: number): void {
    const clamped = Math.max(this.min, Math.min(this.max, value));
    this.value = clamped;
    this.valueText.setText(String(clamped));
    this.refreshButtons();
  }

  getValue(): number {
    return this.value;
  }

  setLabel(text: string): void {
    this.labelText.setText(text);
  }

  setOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  setVisible(visible: boolean): void {
    this.labelText.setVisible(visible);
    this.valueText.setVisible(visible);
    this.minusButton.setVisible(visible);
    this.plusButton.setVisible(visible);
  }

  private step(delta: number): void {
    const next = this.value + delta;
    if (next < this.min || next > this.max) return;
    this.value = next;
    this.valueText.setText(String(next));
    this.refreshButtons();
    this.onChange(next);
  }

  private refreshButtons(): void {
    this.minusButton.setEnabled(this.value > this.min);
    this.plusButton.setEnabled(this.value < this.max);
  }
}
